/**
 * Button Component
 * 
 * Centralized button component for the entire portal
 * - Multiple variants (primary, secondary, outline, ghost, danger)
 * - Three sizes (sm, md, lg)
 * - Icon support (left and right)
 * - Loading state with spinner
 * - Full width option
 * - Accessible by default
 * 
 * @component
 * @example
 * // Primary button with icon
 * <Button variant="primary" iconLeft={<FiPlus />} onClick={handleCreate}>
 *   Criar Usuário
 * </Button>
 * 
 * @example
 * // Secondary button with loading
 * <Button variant="secondary" loading={isLoading} onClick={handleCancel}>
 *   Cancelar
 * </Button>
 * 
 * @example
 * // Outline button small size
 * <Button variant="outline" size="sm" iconLeft={<FiEdit />}>
 *   Editar
 * </Button>
 */

import { FC } from 'react';
import clsx from 'clsx';
import type { ButtonProps } from './Button.types';
import styles from './Button.module.css';

const Button: FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  iconLeft,
  iconRight,
  loading = false,
  fullWidth = false,
  as = 'button',
  children,
  className,
  disabled,
  type = 'button',
  ...props
}) => {
  const Component = as;

  const buttonClasses = clsx(
    styles.button,
    styles[variant],
    styles[size],
    {
      [styles.fullWidth]: fullWidth,
    },
    className
  );

  const isDisabled = disabled || loading;

  return (
    <Component
      className={buttonClasses}
      disabled={isDisabled}
      type={as === 'button' ? type : undefined}
      aria-busy={loading}
      aria-disabled={isDisabled}
      {...(props as any)}
    >
      {loading ? (
        <>
          <span className={styles.spinner} aria-hidden="true" />
          {children}
        </>
      ) : (
        <>
          {iconLeft && (
            <span className={styles.iconLeft} aria-hidden="true">
              {iconLeft}
            </span>
          )}
          {children}
          {iconRight && (
            <span className={styles.iconRight} aria-hidden="true">
              {iconRight}
            </span>
          )}
        </>
      )}
    </Component>
  );
};

Button.displayName = 'Button';

export default Button;